/**
 * Space Worm — how the worm moves (specs/space-worm-mode.md §5.1, §5.2, §5.5).
 *
 * **Free angles, not squares.** The worm has a position in pixels and a heading in radians; it
 * always moves forward at the rung's speed and it turns toward where the finger is, at no more than
 * `turnRate` radians a second. There is no grid anywhere in this mode and there must never be one:
 * a worm that can only face four ways is a worm made of stairs, and the whole pleasure of driving
 * one is the curve.
 *
 * The body is a **trail**: a list of the head's recent positions, from which every bead's position
 * is read back at a fixed arc length behind the head. That one decision buys three things at once —
 * the body follows the exact path the head took, beads stay evenly spaced whatever the speed, and
 * growing the worm costs nothing but a larger bead count.
 *
 * **There is no wrap.** The board used to be a torus and it was the wrong shape for a thing you
 * drive: leaving the right edge and reappearing on the left is not a move a child can plan, and it
 * put the worm somewhere they were not looking. The board is now a plain rectangle with a margin
 * around it — the worm may slip out of sight, a marker says where it went, and pointing anywhere
 * brings it straight back in (§5.2).
 *
 * Two rules do the work the grid version's queue and reversal rules used to:
 *
 *  - **The turn rate is the only limit on steering.** The scene derives it from a fixed turning
 *    RADIUS (`speed / WORM.turnRadius`), which is also what makes a knot *possible*: at full tilt
 *    the worm carves a circle of that radius, and a body longer than its circumference can catch
 *    itself (§5.5). Every other way of dying was removed on purpose.
 *  - **The neck is not solid.** The first `neckBeads` beads cannot be hit, because they are the ones
 *    the head is always a hair away from. Without that, the tightest legal turn kills you.
 *
 * Phaser-free.
 */

export interface Pt {
  x: number;
  y: number;
}

/** The play area: a plain rectangle, with the outside just outside it (§5.2). */
export interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Worm {
  head: Pt;
  /** Radians. 0 points right, and it grows clockwise because y grows downward. */
  angle: number;
  /** Where the head has been, newest first, already wrapped. The body is read off this. */
  trail: Pt[];
  /** How many beads the worm has, head included. */
  beads: number;
}

/** How often the trail takes a sample, in pixels. Small enough that a bead never cuts a corner. */
export const SAMPLE_PX = 4;

/** Is this point on the visible board? */
export function inside(p: Pt, b: Bounds): boolean {
  return p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h;
}

/**
 * How far outside the board a point is — 0 when it is on it.
 *
 * What the leash of §5.2 measures: past `WORM.outsideMargin` the worm turns itself round and comes
 * back, so it can slip off the edge but never wander away.
 */
export function outsideBy(p: Pt, b: Bounds): number {
  const dx = Math.max(b.x - p.x, 0, p.x - (b.x + b.w));
  const dy = Math.max(b.y - p.y, 0, p.y - (b.y + b.h));
  return Math.hypot(dx, dy);
}

/** The nearest point of the board, `inset` in from its edge — where a worm outside is sent back to. */
export function nearestInside(p: Pt, b: Bounds, inset = 0): Pt {
  return {
    x: Math.min(Math.max(p.x, b.x + inset), b.x + b.w - inset),
    y: Math.min(Math.max(p.y, b.y + inset), b.y + b.h - inset),
  };
}

/** The vector from `a` to `b`. A straight line: the board is a rectangle, not a torus. */
export function delta(a: Pt, b: Pt, _bounds?: Bounds): Pt {
  return { x: b.x - a.x, y: b.y - a.y };
}

export function dist(a: Pt, b: Pt, _bounds?: Bounds): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Difference between two headings, normalised to (-π, π]. */
export function angleDiff(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d <= -Math.PI) d += Math.PI * 2;
  return d;
}

/**
 * A worm of `beads` beads with its head at `head`, its body laid out straight behind it so it is
 * already travelling along `angle`.
 *
 * Used at the start of a run, and again at the neutral respawn a knot asks for (§5.5.1). A worm
 * that lost its word to a wrong BITE keeps its position and heading and only gives back its beads
 * — that is `shrinkTo`, below.
 */
export function newWorm(head: Pt, angle: number, beads: number, spacing: number, bounds: Bounds): Worm {
  const back = { x: -Math.cos(angle), y: -Math.sin(angle) };
  const trail: Pt[] = [];
  // Two beads' worth of slack, so the first frames have a body to read positions from.
  const length = (beads + 2) * spacing;
  for (let d = 0; d <= length; d += SAMPLE_PX) {
    trail.push({ x: head.x + back.x * d, y: head.y + back.y * d });
  }
  void bounds;
  return { head: { ...head }, angle, trail, beads };
}

/**
 * Turn toward `target` — a point, not a direction, because that is what a finger is (§5.1).
 *
 * The turn is capped at `turnRate` radians per second — the caller derives it from the rung's speed
 * and `WORM.turnRadius`, so the tightest circle is the same size at every rung. `null` holds the
 * current heading.
 *
 * **Steering is the one measurement in this module that does NOT take the wrap.** Everything else —
 * collisions, tile distances, the body — is measured the short way round the torus, because that is
 * where things physically are. But a finger is a place on a screen: point at the far side and the
 * worm must set off *across* the board toward it, not turn round and leave by the near edge because
 * that route happens to be twelve pixels shorter. The player cannot see the torus; they can see
 * where they pointed (§17.13).
 */
export function steer(w: Worm, target: Pt | null, turnRate: number, dt: number, bounds: Bounds): void {
  if (!target) return;
  void bounds;
  const d = { x: target.x - w.head.x, y: target.y - w.head.y };
  if (Math.hypot(d.x, d.y) < 1) return;
  const want = Math.atan2(d.y, d.x);
  const diff = angleDiff(w.angle, want);
  const max = turnRate * dt;
  w.angle += Math.abs(diff) <= max ? diff : Math.sign(diff) * max;
}

/**
 * Straight-line distance, ignoring the wrap — the one steering uses (see `steer`).
 */
export function directDist(a: Pt, b: Pt): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Has the head arrived at the point it was steering at, or just gone past it?
 *
 * A destination the worm never lets go of is a destination it **orbits**: it arrives, overshoots by
 * its own turning radius, comes round, overshoots again. That is the single most irritating thing a
 * worm can do, and it is what "go there" must not mean (§5.1.3).
 *
 * Two ways to be there, and the second is the one that matters. Close enough is obvious. The other
 * is *the distance started growing again while still nearby* — which is exactly what the top of an
 * orbit looks like, and it catches the case where the worm can never get inside `radius` because
 * its turning circle is wider than that.
 */
export function reachedTarget(
  head: Pt, target: Pt, lastDist: number, radius: number, near: number,
): boolean {
  const d = directDist(head, target);
  return d < radius || (d < near && d > lastDist);
}

/** Turn toward a heading rather than a point — what the arrow keys ask for. */
export function steerAngle(w: Worm, want: number, turnRate: number, dt: number): void {
  const diff = angleDiff(w.angle, want);
  const max = turnRate * dt;
  w.angle += Math.abs(diff) <= max ? diff : Math.sign(diff) * max;
}

/** Move forward and record the path. Call once a frame; `dt` is in seconds. */
export function advance(w: Worm, speed: number, dt: number, spacing: number, bounds: Bounds): void {
  const step = speed * dt;
  void bounds;
  w.head = { x: w.head.x + Math.cos(w.angle) * step, y: w.head.y + Math.sin(w.angle) * step };
  if (w.trail.length === 0 || dist(w.head, w.trail[0], bounds) >= SAMPLE_PX) {
    w.trail.unshift({ ...w.head });
  }
  // Keep a little more trail than the body needs, and no more: this list is walked every frame.
  const keep = Math.ceil(((w.beads + 2) * spacing) / SAMPLE_PX) + 4;
  if (w.trail.length > keep) w.trail.length = keep;
}

/**
 * Where bead `i` is: `i * spacing` back along the path the head actually took.
 *
 * Reading the body off the trail rather than storing it is what makes the body follow a curve
 * exactly, and what makes growing the worm a one-line change.
 */
export function beadAt(w: Worm, i: number, spacing: number, bounds: Bounds): Pt {
  const want = i * spacing;
  if (want <= 0) return { ...w.head };
  let walked = 0;
  let from: Pt = w.head;
  for (const p of w.trail) {
    const d = delta(from, p, bounds);
    const seg = Math.hypot(d.x, d.y);
    if (seg > 0 && walked + seg >= want) {
      const t = (want - walked) / seg;
      return { x: from.x + d.x * t, y: from.y + d.y * t };
    }
    walked += seg;
    from = p;
  }
  return { ...from };
}

export function beadPositions(w: Worm, spacing: number, bounds: Bounds): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < w.beads; i++) out.push(beadAt(w, i, spacing, bounds));
  return out;
}

/**
 * Has the head run into its own body (§5.5)?
 *
 * `neck` beads are skipped: those are the ones the head is always a hair away from, and counting
 * them would make the tightest legal turn fatal. Everything past the neck is solid, which is what
 * makes a tight circle held too long the one way to lose that is not a misreading.
 */
export function selfHit(
  w: Worm, spacing: number, bounds: Bounds, neck: number, radius: number,
): Pt | null {
  for (let i = neck; i < w.beads; i++) {
    const b = beadAt(w, i, spacing, bounds);
    if (dist(w.head, b, bounds) < radius) return b;
  }
  return null;
}

/**
 * The bead the head is about to reach if it holds this curve — the warning of §5.5 rule 3.
 *
 * Looks `ahead` pixels along the CURRENT heading (a straight probe, not the arc: a child reads the
 * warning as "that bit, there", and a curved prediction would light up beads they are turning away
 * from). A death nobody saw coming is a death the child blames on the game.
 */
export function dangerAhead(
  w: Worm, spacing: number, bounds: Bounds, neck: number, ahead: number, radius: number,
): Pt | null {
  const probe = {
    x: w.head.x + Math.cos(w.angle) * ahead,
    y: w.head.y + Math.sin(w.angle) * ahead,
  };
  let best: Pt | null = null;
  let bestD = Infinity;
  for (let i = neck; i < w.beads; i++) {
    const b = beadAt(w, i, spacing, bounds);
    const d = dist(probe, b, bounds);
    if (d < radius && d < bestD) { best = b; bestD = d; }
  }
  return best;
}

/**
 * Is the ray from the head along `angle` clear of the worm's own body for `probe` pixels?
 *
 * The neck is skipped, as everywhere: it is always a hair away and counting it would call every
 * heading blocked.
 */
export function rayClear(
  w: Worm, angle: number, probe: number, spacing: number, bounds: Bounds, neck: number, radius: number,
): boolean {
  const dir = { x: Math.cos(angle), y: Math.sin(angle) };
  const solid: Pt[] = [];
  for (let i = neck; i < w.beads; i++) solid.push(beadAt(w, i, spacing, bounds));
  if (solid.length === 0) return true;
  for (let step = 0.2; step <= 1.0001; step += 0.2) {
    const at = { x: w.head.x + dir.x * probe * step, y: w.head.y + dir.y * probe * step };
    if (solid.some((b) => dist(at, b) < radius)) return false;
  }
  return true;
}

/**
 * How fast the leash may turn the worm, given how long it is (§5.2.2).
 *
 * A turn is only safe from the worm's own tail if the circle it carves is **longer than the body**:
 * at the top of a tighter circle the head comes back onto its own path and meets whatever is lying
 * there. So the leash's turning radius is never smaller than `body / 2π` — which means a long worm
 * comes home in a wide, slow arc and a short one whips round.
 *
 * That is also the right feel. Twenty segments of worm *should* be unwieldy; it is the thing the
 * child has built, and the run has been making it harder to fly since the fourth letter (§5.5).
 */
export function leashTurnRate(
  w: Worm, speed: number, turnRadius: number, spacing: number, hitRadius: number,
): number {
  const body = w.beads * spacing;
  const safeRadius = body / (Math.PI * 2) + hitRadius * 0.5;
  return speed / Math.max(turnRadius, safeRadius);
}

/**
 * A heading that takes the worm back toward the board **without sweeping through its own body**
 * (§5.2.2).
 *
 * The leash cannot simply point at the nearest edge: off the board the worm turns at 2.6x its
 * normal rate, and a hard turn toward the board with the body in the way would curl the head
 * straight into its own tail — a knot the child never saw, out where they cannot see anything.
 *
 * So the way home is chosen rather than computed: the direct heading first, then progressively
 * wider deviations either side of it, and the first one whose ray is clear of the body wins. Every
 * candidate still has to make progress toward the board, so "clear" can never mean "further out".
 * If nothing is clear — which needs a worm wrapped round its own head — the direct heading is
 * returned and §5.5's rule that a knot only counts on the board covers the rest.
 */
export function safeReturnAngle(
  w: Worm, bounds: Bounds, inset: number, spacing: number, probe: number, neck: number, radius: number,
): number {
  const target = nearestInside(w.head, bounds, inset);
  const want = Math.atan2(target.y - w.head.y, target.x - w.head.x);
  const stepRad = Math.PI / 12;   // 15°
  for (let i = 0; i <= 8; i++) {
    for (const side of i === 0 ? [0] : [1, -1]) {
      const a = want + side * i * stepRad;
      // Never a heading that gives up on coming back: 8 × 15° either side stays inside a
      // half-circle of the direct route, so every candidate still closes on the board.
      if (rayClear(w, a, probe, spacing, bounds, neck, radius)) return a;
    }
  }
  return want;
}

/**
 * Give the worm back its starting length where it stands, keeping its position and heading (§5.4).
 *
 * For a wrong bite. **Not for a knot**: this straightens the body but leaves the head buried in the
 * curl it died in and still pointing into it, which is how one knot used to take every remaining
 * book (§5.5.1). A knot respawns with `newWorm` at the neutral position instead.
 */
export function shrinkTo(w: Worm, beads: number, spacing: number, bounds: Bounds): Worm {
  const fresh = newWorm(w.head, w.angle, beads, spacing, bounds);
  w.trail = fresh.trail;
  w.beads = beads;
  return w;
}
